import { useState, useEffect, useCallback } from 'react';
import { Loader, Search, X, Plus, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { SourceQuery, WorkspaceSection } from '../lib/types';
import { formatDateTime } from '../lib/text';
import DismissableInfoCard from './DismissableInfoCard';

// The Searches tab: the searches Navaris runs on a source at each sync. What they find
// Navaris judges, and the items worth reading feed the memory for the search's area.
interface Props {
    sourceId: string;
}

function flatten(sections: WorkspaceSection[], depth = 0): { id: string; name: string }[] {
    return sections.flatMap(s => [
        { id: s.id, name: `${depth ? '- '.repeat(depth) : ''}${s.name}` },
        ...flatten(s.subsections || [], depth + 1),
    ]);
}

export default function SourceQueriesTab({ sourceId }: Props) {
    const { authFetch } = useAuth();
    const { showToast } = useToast();
    const { sections } = useWorkspace();
    const [queries, setQueries] = useState<SourceQuery[]>([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState('');
    const [area, setArea] = useState('');
    const [saving, setSaving] = useState(false);
    const areas = flatten(sections);
    const areaName = (id: string | null) => (id && areas.find(a => a.id === id)?.name.replace(/^(- )+/, '')) || 'The company';

    const load = useCallback(async () => {
        try {
            const res = await authFetch(`/sources/${sourceId}/queries`);
            if (!res.ok) throw new Error('Failed to load searches');
            const data = await res.json();
            setQueries(data.queries || []);
        } catch (err) {
            console.error('Failed to load searches:', err);
        } finally {
            setLoading(false);
        }
    }, [authFetch, sourceId]);

    useEffect(() => { load(); }, [load]);

    const add = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!text.trim()) return;
        setSaving(true);
        try {
            const res = await authFetch(`/sources/${sourceId}/queries`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: text.trim(), section_id: area || null }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'Could not add the search');
            }
            setText('');
            await load();
        } catch (err) {
            showToast(err instanceof Error ? err.message : 'Could not add the search', 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (q: SourceQuery) => {
        const res = await authFetch(`/sources/${sourceId}/queries/${q.id}`, { method: 'DELETE' });
        if (res.ok) setQueries(prev => prev.filter(x => x.id !== q.id));
        else showToast('Could not remove the search', 'error');
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader className="w-8 h-8 animate-spin text-brand" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <DismissableInfoCard storageKey="source-searches-info" title="Searches">
                At each sync Navaris runs these searches on the source for anything new, reads what
                is worth keeping and adds it to the memory for the search's area. Ask Navaris in a
                chat to follow a subject and it adds the search here.
            </DismissableInfoCard>

            <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
                <input
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Search for, such as a customer's name or 'board meeting'"
                    className="flex-1 text-sm px-3 py-2 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand placeholder:text-coolslate"
                />
                <select
                    value={area}
                    onChange={e => setArea(e.target.value)}
                    className="text-sm px-3 py-2 border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
                    aria-label="Business area"
                >
                    <option value="">The company</option>
                    {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <button
                    type="submit"
                    disabled={saving || !text.trim()}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-lg bg-brand text-white hover:bg-brand-dark disabled:opacity-40 cursor-pointer"
                >
                    {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Add search
                </button>
            </form>

            {queries.length === 0 ? (
                <p className="text-center text-coolslate py-8">
                    No searches yet. Add one above for Navaris to run at each sync.
                </p>
            ) : (
                <div data-agent-view className="border border-softgrey rounded-lg bg-white divide-y divide-softgrey overflow-hidden">
                    {queries.map(q => (
                        <div key={q.id} className="flex items-start gap-3 px-3 py-3">
                            <Search className="w-4 h-4 mt-0.5 shrink-0 text-coolslate" aria-hidden />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-graphite break-words">{q.text}</div>
                                <div className="text-xs text-coolslate mt-0.5 flex flex-wrap gap-x-3">
                                    <span>{areaName(q.section_id)}</span>
                                    {q.origin === 'default' && (
                                        <span className="inline-flex items-center gap-1">
                                            <Sparkles className="w-3 h-3 text-brand-dark" aria-hidden />Default search
                                        </span>
                                    )}
                                    <span>{q.hits} found, {q.read} read</span>
                                    <span>{q.last_run_at ? `Last run ${formatDateTime(q.last_run_at)}` : 'Not run yet'}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => remove(q)}
                                className="p-1 text-coolslate hover:text-graphite cursor-pointer"
                                aria-label={`Stop searching for ${q.text}`}
                                title="Stop this search"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
